import type { BehaviorDef, BossPhaseDef, BossSpecialDef, EmitterDef, EnemyDef, Formation, PatternDef } from '../../content/types';
import { AFFIXES, AFFIX_BITS } from '../../content/affixes';
import { TAU } from '../../core/math';
import { nearestPlayer, type SimCtx } from '../context';
import { MAX_EMITTERS, SIDE_ENEMY, wrapCoord, wrapDelta } from '../world';
import { BULLET_VISUALS, emitBurst } from './projectiles';
import { HEART_HEAL_FRACTION } from './pickups';
import { datan2, dcos, dhypot, dsin } from '../../core/dmath';

/** Ordinary enemies rarely drop hearts; keep the existing elite/boss reward frequency. */
export const HEART_DROP_CHANCE = 0.01;

/** Elite multipliers (applied on top of stage scaling). */
export const ELITE_HP_MULT = 4;
export const ELITE_DMG_MULT = 1.5;
export const ELITE_SCALE = 1.6;
export const ELITE_XP_MULT = 5;

/** Elite affixes: how many stack (1, +1 more at 25% chance) and the shield fraction of max HP. */
const AFFIX_BASE_COUNT = 1;
const AFFIX_EXTRA_CHANCE = 0.25;
const AFFIX_SHIELD_FRACTION = 0.5;
const AFFIX_HASTE_SPEED_MULT = 1.4;
const AFFIX_HASTE_COOLDOWN_MULT = 0.6;
const AFFIX_SPLIT_COUNT = 3;
const AFFIX_SPLIT_SPREAD = 1.2;

/** 'volatile' affix: synthetic pattern emitter fired once on death (not a registered content pattern). */
const VOLATILE_EMITTER: EmitterDef = {
  shape: 'radial',
  interval: 0,
  bursts: 1,
  count: 12,
  bullet: { speed: 5, lifetime: 2, radius: 0.3, damage: 12, visual: 'orb', color: 0xff8040 },
};

/** Roll 1 (+1 at 25% chance) unique weighted affixes for a freshly-spawned elite. Deterministic via ctx.rng. */
function rollAffixes(ctx: SimCtx): number {
  let mask = 0;
  const n = AFFIX_BASE_COUNT + (ctx.rng.chance(AFFIX_EXTRA_CHANCE) ? 1 : 0);
  for (let k = 0; k < n; k++) {
    const avail = AFFIXES.filter((a) => (mask & AFFIX_BITS[a.id]) === 0);
    if (avail.length === 0) break;
    const idx = ctx.rng.weightedIndex(avail.map((a) => a.weight));
    mask |= AFFIX_BITS[avail[idx].id];
  }
  return mask;
}

/** 'volatile' affix: fire a radial burst of slow bullets at the death position. */
function fireVolatileBurst(ctx: SimCtx, x: number, y: number): void {
  const b = ctx._burst;
  b.side = SIDE_ENEMY;
  b.damage = VOLATILE_EMITTER.bullet.damage;
  emitBurst(b, VOLATILE_EMITTER, x, y, 0, 0);
}

/** Derive a pattern's total duration from its emitters (cached by Sim). */
export function patternDuration(p: PatternDef): number {
  if (p.duration !== undefined) return p.duration;
  let end = 0;
  for (const em of p.emitters) {
    const t = (em.start ?? 0) + em.interval * Math.max(0, em.bursts - 1);
    if (t > end) end = t;
  }
  return end + 0.05;
}

/** Spawn position helper for formations. Writes into out[0], out[1]. */
export function formationPos(ctx: SimCtx, formation: Formation, k: number, n: number, out: Float32Array): void {
  const r = ctx.w.arenaRadius;
  const rng = ctx.rng;
  const np = nearestPlayer(ctx, 0, 0);
  const cx = np.idx >= 0 ? ctx.w.players[np.idx].x : 0;
  const cy = np.idx >= 0 ? ctx.w.players[np.idx].y : 0;
  switch (formation) {
    case 'ring': {
      const a = (k / n) * TAU + rng.range(0, 0.2);
      const d = 18;
      out[0] = cx + dcos(a) * d;
      out[1] = cy + dsin(a) * d;
      break;
    }
    case 'cluster': {
      if (k === 0 || !ctx._clusterSet) {
        const a = rng.range(0, TAU);
        ctx._cluster[0] = cx + dcos(a) * 16;
        ctx._cluster[1] = cy + dsin(a) * 16;
        ctx._clusterSet = true;
      }
      out[0] = ctx._cluster[0] + rng.range(-3, 3);
      out[1] = ctx._cluster[1] + rng.range(-3, 3);
      if (k === n - 1) ctx._clusterSet = false;
      break;
    }
    case 'line': {
      const a = rng.range(0, TAU);
      const px = -dsin(a);
      const py = dcos(a);
      const t = (k / Math.max(1, n - 1) - 0.5) * 16;
      out[0] = cx + dcos(a) * 17 + px * t;
      out[1] = cy + dsin(a) * 17 + py * t;
      break;
    }
    case 'random': {
      const a = rng.range(0, TAU);
      const d = rng.range(12, 26);
      out[0] = cx + dcos(a) * d;
      out[1] = cy + dsin(a) * d;
      break;
    }
    case 'edge':
    default: {
      // just outside the visible area around the player, clamped to the arena
      const a = rng.range(0, TAU);
      const d = 20;
      out[0] = cx + dcos(a) * d;
      out[1] = cy + dsin(a) * d;
    }
  }
  out[0] = wrapCoord(out[0], r);
  out[1] = wrapCoord(out[1], r);
}

export function spawnEnemy(ctx: SimCtx, defId: string, x: number, y: number, elite = false, objectiveWave = false): number {
  const def = ctx.reg.enemies.get(defId);
  if (!def) return 0;
  const e = ctx.w.enemies;
  const i = e.pool.alloc();
  if (i === 0) return 0;
  const isElite = elite || !!def.elite;
  e.x[i] = x;
  e.y[i] = y;
  e.px[i] = x;
  e.py[i] = y;
  e.vx[i] = 0;
  e.vy[i] = 0;
  e.maxHp[i] = def.hp * ctx.scale.hp * (isElite ? ELITE_HP_MULT : 1);
  e.hp[i] = e.maxHp[i];
  e.radius[i] = def.radius * (isElite ? ELITE_SCALE : 1);
  e.speed[i] = def.speed * ctx.scale.speed;
  e.def[i] = ctx.reg.enemyIndex.get(defId)!;
  e.elite[i] = isElite ? 1 : 0;
  e.isBoss[i] = 0;
  e.objectiveWave[i] = objectiveWave ? 1 : 0;
  e.gen[i]++;
  e.state[i] = 0;
  e.timer[i] = 0;
  e.facing[i] = datan2(-y, -x);
  e.age[i] = 0;
  e.slow[i] = 0;
  e.slowT[i] = 0;
  e.burnDps[i] = 0;
  e.burnT[i] = 0;
  e.hitFlash[i] = 0;
  e.contactCd[i] = 0;
  e.patternIdx[i] = -1;
  e.patternT[i] = 0;
  e.patternSlot[i] = 0;
  e.patternCd[i] = ctx.rng.range(0.3, 1.2); // desync fresh spawns
  e.phase[i] = 0;
  e.phaseT[i] = 0;
  e.phaseEntryFired[i] = 0;
  e.summonT[i] = 0;
  e.invuln[i] = 0;
  e.z[i] = def.behavior.type === 'dive' ? def.behavior.height : 0;
  e.specialT[i] = 0;
  e.specialActive[i] = 0;
  e.trailT[i] = 0;
  e.shieldAdds[i] = 0;
  e.protects[i] = 0;
  e.affixes[i] = 0;
  e.shield[i] = 0;
  if (isElite) {
    const mask = rollAffixes(ctx);
    e.affixes[i] = mask;
    if (mask & AFFIX_BITS.shielded) e.shield[i] = e.maxHp[i] * AFFIX_SHIELD_FRACTION;
    if (mask & AFFIX_BITS.hasted) e.speed[i] *= AFFIX_HASTE_SPEED_MULT;
  }
  if (def.behavior.type === 'burrow') e.timer[i] = def.behavior.surfaceTime;
  if (def.behavior.type === 'blink') e.timer[i] = def.behavior.interval * ctx.rng.range(0.5, 1);
  e.momX[i] = 0;
  e.momY[i] = 0;
  if (def.behavior.type === 'ambush') e.z[i] = -1; // buried until triggered
  if (def.behavior.type === 'march') e.timer[i] = def.behavior.stepTime * ctx.rng.range(0.3, 1);
  if (def.behavior.type === 'ram') e.timer[i] = def.behavior.aimTime * ctx.rng.range(0.3, 1);
  if (def.behavior.type === 'flyover') e.timer[i] = def.behavior.loiterTime * ctx.rng.range(0.3, 1);
  if (def.behavior.type === 'assemble') e.timer[i] = def.behavior.interval * ctx.rng.range(0.3, 1);
  if (def.behavior.type === 'lockon') e.timer[i] = def.behavior.cooldown * ctx.rng.range(0.2, 1);
  if (def.behavior.type === 'gridstep') e.timer[i] = def.behavior.switchTime * ctx.rng.range(0.3, 1);
  if (def.behavior.type === 'lag') {
    e.timer[i] = def.behavior.lagTime * ctx.rng.range(0.3, 1);
    // avoid chasing stale leapX/leapY left over from a previous occupant of this pool slot
    e.leapX[i] = x;
    e.leapY[i] = y;
  }
  if (def.behavior.type === 'unwind') {
    e.timer[i] = def.behavior.advanceTime * ctx.rng.range(0.3, 1);
    e.leapX[i] = x; // the point it will later snap back to
    e.leapY[i] = y;
  }
  return i;
}

export function spawnGroup(ctx: SimCtx, defId: string, count: number, formation: Formation, elite = false, objectiveWave = false): void {
  const pos = ctx._pos;
  for (let k = 0; k < count; k++) {
    formationPos(ctx, formation, k, count, pos);
    spawnEnemy(ctx, defId, pos[0], pos[1], elite, objectiveWave);
  }
}

export function spawnBoss(ctx: SimCtx, bossId: string): number {
  const def = ctx.reg.bosses.get(bossId);
  if (!def) return 0;
  const e = ctx.w.enemies;
  const i = e.pool.alloc();
  if (i === 0) return 0;
  const np = nearestPlayer(ctx, 0, 0);
  const px = np.idx >= 0 ? ctx.w.players[np.idx].x : 0;
  const py = np.idx >= 0 ? ctx.w.players[np.idx].y : 0;
  const a = datan2(-py, -px);
  e.x[i] = wrapCoord(px + dcos(a) * 16, ctx.w.half);
  e.y[i] = wrapCoord(py + dsin(a) * 16, ctx.w.half);
  e.px[i] = e.x[i];
  e.py[i] = e.y[i];
  e.vx[i] = 0;
  e.vy[i] = 0;
  // boss HP uses the boss scaling (documented in DESIGN.md)
  e.maxHp[i] = def.hp * ctx.scale.hp * ctx.scale.bossHp * bossCoopMult(ctx.playerCount) / coopHpMult(ctx.playerCount);
  e.hp[i] = e.maxHp[i];
  e.radius[i] = def.radius;
  e.speed[i] = def.speed * ctx.scale.speed;
  e.def[i] = -1;
  e.elite[i] = 0;
  e.isBoss[i] = 1;
  e.objectiveWave[i] = 0;
  e.gen[i]++;
  e.state[i] = 0;
  e.timer[i] = 0;
  e.facing[i] = a + Math.PI;
  e.age[i] = 0;
  e.slow[i] = 0;
  e.slowT[i] = 0;
  e.burnDps[i] = 0;
  e.burnT[i] = 0;
  e.hitFlash[i] = 0;
  e.contactCd[i] = 0;
  e.patternIdx[i] = -1;
  e.patternT[i] = 0;
  e.patternSlot[i] = 0;
  e.patternCd[i] = 1.5;
  e.phase[i] = 0;
  e.phaseT[i] = 0;
  e.phaseEntryFired[i] = 0;
  e.summonT[i] = 0;
  e.invuln[i] = def.phases[0].transition ?? 1.5;
  e.z[i] = 0;
  e.specialT[i] = 0;
  e.specialActive[i] = 0;
  e.trailT[i] = 0;
  e.shieldAdds[i] = 0;
  e.protects[i] = 0;
  e.affixes[i] = 0; // bosses never roll elite affixes
  e.shield[i] = 0;
  enterBossPhase(ctx, i, 0, def);
  ctx.boss = { def, slot: i };
  ctx.events.push({ type: 'bossSpawn', defId: def.id });
  ctx.events.push({ type: 'shake', strength: 0.6 });
  return i;
}

/** Co-op scaling formulas (see docs/DESIGN.md). */
export function coopHpMult(players: number): number {
  return 1 + 0.6 * (players - 1);
}
export function coopDensityMult(players: number): number {
  return 1 + 0.5 * (players - 1);
}
export function bossCoopMult(players: number): number {
  return 1 + 0.75 * (players - 1);
}

/* ------------------------------------------------------------------------ */

const STATE_APPROACH = 0;
const STATE_WINDUP = 1;
const STATE_CHARGE = 2;
const STATE_COOLDOWN = 3;
const STATE_HOVER = 0;
const STATE_DIVE = 1;
const STATE_RISE = 2;

// Scratch (module-level, single-threaded sim) — avoids allocating per enemy per tick.
let curBehavior: BehaviorDef;
let curDef: EnemyDef | null = null;
let curPhase: BossPhaseDef | null = null;

function resolveBehavior(ctx: SimCtx, i: number): void {
  const e = ctx.w.enemies;
  if (e.isBoss[i] && ctx.boss) {
    curPhase = ctx.boss.def.phases[e.phase[i]];
    curBehavior = curPhase.behavior;
    curDef = null;
    return;
  }
  curDef = ctx.reg.enemyList[e.def[i]];
  curBehavior = curDef.behavior;
  curPhase = null;
}

export function updateEnemies(ctx: SimCtx, dt: number): void {
  const w = ctx.w;
  const e = w.enemies;
  const rng = ctx.rng;
  const high = e.pool.high;
  const half = w.half;

  for (let i = 1; i < high; i++) {
    if (!e.pool.alive[i]) continue;
    e.px[i] = e.x[i];
    e.py[i] = e.y[i];
    e.age[i] += dt;
    if (e.hitFlash[i] > 0) e.hitFlash[i] -= dt;
    if (e.contactCd[i] > 0) e.contactCd[i] -= dt;
    if (e.invuln[i] > 0) e.invuln[i] -= dt;

    // status effects
    if (e.slowT[i] > 0) {
      e.slowT[i] -= dt;
      if (e.slowT[i] <= 0) e.slow[i] = 0;
    }
    if (e.burnT[i] > 0) {
      e.burnT[i] -= dt;
      e.hp[i] -= e.burnDps[i] * dt;
      if (e.hp[i] <= 0) {
        killEnemy(ctx, i, -1);
        continue;
      }
    }

    resolveBehavior(ctx, i);
    const behavior = curBehavior;
    const def = curDef;
    const phase = curPhase;
    const speedMult = (1 - e.slow[i]) * (phase?.speedMult ?? 1);
    const spd = e.speed[i] * speedMult;
    const np = nearestPlayer(ctx, e.x[i], e.y[i]);
    const hasTarget = np.idx >= 0;
    const dist = Math.sqrt(np.d2);
    const dirx = hasTarget && dist > 1e-4 ? np.dx / dist : 0;
    const diry = hasTarget && dist > 1e-4 ? np.dy / dist : 0;
    let vx = 0;
    let vy = 0;

    switch (behavior.type) {
      case 'chase':
        vx = dirx * spd;
        vy = diry * spd;
        break;
      case 'keepDistance': {
        const want = behavior.distance;
        let f = 0;
        if (dist > want + 1.5) f = 1;
        else if (dist < want - 1.5) f = -1;
        const strafe = behavior.strafe ?? 0;
        vx = (dirx * f - diry * strafe) * spd;
        vy = (diry * f + dirx * strafe) * spd;
        break;
      }
      case 'charge': {
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case STATE_APPROACH:
            vx = dirx * spd;
            vy = diry * spd;
            if (dist < 7) {
              e.state[i] = STATE_WINDUP;
              e.timer[i] = behavior.windup;
              e.facing[i] = datan2(diry, dirx);
            }
            break;
          case STATE_WINDUP:
            // track slowly during windup so the charge is aimable but dodgeable
            e.facing[i] += ((datan2(diry, dirx) - e.facing[i] + 3 * Math.PI) % TAU - Math.PI) * 2 * dt;
            if (e.timer[i] <= 0) {
              e.state[i] = STATE_CHARGE;
              e.timer[i] = behavior.duration;
            }
            break;
          case STATE_CHARGE:
            vx = dcos(e.facing[i]) * spd * behavior.speedMult;
            vy = dsin(e.facing[i]) * spd * behavior.speedMult;
            if (e.timer[i] <= 0) {
              e.state[i] = STATE_COOLDOWN;
              e.timer[i] = behavior.cooldown;
            }
            break;
          default:
            vx = dirx * spd * 0.3;
            vy = diry * spd * 0.3;
            if (e.timer[i] <= 0) e.state[i] = STATE_APPROACH;
        }
        break;
      }
      case 'orbit': {
        const want = behavior.radius;
        const f = dist > want + 1 ? 1 : dist < want - 1 ? -1 : 0;
        vx = (dirx * f * 0.7 - diry * behavior.angular) * spd;
        vy = (diry * f * 0.7 + dirx * behavior.angular) * spd;
        break;
      }
      case 'drift': {
        e.timer[i] -= dt;
        if (e.timer[i] <= 0) {
          e.timer[i] = rng.range(0.5, 1.5);
          e.facing[i] = rng.range(0, TAU);
        }
        vx = dcos(e.facing[i]) * spd * behavior.jitter;
        vy = dsin(e.facing[i]) * spd * behavior.jitter;
        break;
      }
      case 'dive': {
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case STATE_HOVER:
            e.z[i] = behavior.height;
            vx = dirx * spd * 0.8;
            vy = diry * spd * 0.8;
            if (e.timer[i] <= 0 && dist < 12) {
              e.state[i] = STATE_DIVE;
              e.facing[i] = datan2(diry, dirx);
              e.timer[i] = 0.9;
            }
            break;
          case STATE_DIVE:
            vx = dcos(e.facing[i]) * spd * behavior.speedMult;
            vy = dsin(e.facing[i]) * spd * behavior.speedMult;
            e.z[i] = Math.max(0, e.z[i] - behavior.height * 2.5 * dt);
            if (e.timer[i] <= 0) {
              e.state[i] = STATE_RISE;
              e.timer[i] = behavior.cooldown;
            }
            break;
          default:
            vx = dcos(e.facing[i]) * spd;
            vy = dsin(e.facing[i]) * spd;
            e.z[i] = Math.min(behavior.height, e.z[i] + behavior.height * 1.5 * dt);
            if (e.timer[i] <= 0) e.state[i] = STATE_HOVER;
        }
        break;
      }
      case 'stationary':
        break;
      case 'burrow': {
        // state 0 = surfaced (normal chase), 1 = burrowed (fast, untargetable, z = -1)
        e.timer[i] -= dt;
        if (e.state[i] === 0) {
          e.z[i] = 0;
          vx = dirx * spd;
          vy = diry * spd;
          if (e.timer[i] <= 0) {
            e.state[i] = 1;
            e.timer[i] = behavior.burrowTime;
            e.z[i] = -1;
            ctx.events.push({ type: 'sfx', id: 'burrow', x: e.x[i], y: e.y[i] });
          }
        } else {
          e.z[i] = -1;
          vx = dirx * spd * behavior.speedMult;
          vy = diry * spd * behavior.speedMult;
          if (e.timer[i] <= 0 || dist < behavior.emergeRange) {
            e.state[i] = 0;
            e.timer[i] = behavior.surfaceTime;
            e.z[i] = 0;
            e.hitFlash[i] = 0.25; // emerge flash doubles as the telegraph
            e.contactCd[i] = 0.4; // no instant contact damage on emerge
            ctx.events.push({ type: 'sfx', id: 'emerge', x: e.x[i], y: e.y[i] });
          }
        }
        break;
      }
      case 'blink': {
        e.timer[i] -= dt;
        const want = behavior.distance;
        let f = 0;
        if (dist > want + 1.5) f = 1;
        else if (dist < want - 1.5) f = -1;
        vx = dirx * f * spd;
        vy = diry * f * spd;
        if (e.timer[i] <= 0 && hasTarget) {
          e.timer[i] = behavior.interval;
          const a = rng.range(0, TAU);
          const pl = w.players[np.idx];
          e.x[i] = wrapCoord(pl.x + dcos(a) * behavior.range, half);
          e.y[i] = wrapCoord(pl.y + dsin(a) * behavior.range, half);
          e.px[i] = e.x[i];
          e.py[i] = e.y[i];
          e.invuln[i] = 0.25; // blink flash
          e.patternCd[i] = Math.min(e.patternCd[i], 0.3); // shoot soon after arriving
          ctx.events.push({ type: 'sfx', id: 'blink', x: e.x[i], y: e.y[i] });
        }
        break;
      }
      case 'zigzag': {
        const s = dsin((e.age[i] * TAU) / behavior.period + i);
        vx = (dirx - diry * s * behavior.amp) * spd;
        vy = (diry + dirx * s * behavior.amp) * spd;
        break;
      }
      case 'leap': {
        // state 0 = approach, 1 = windup (telegraph), 2 = airborne, 3 = cooldown
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd;
            vy = diry * spd;
            if (dist < behavior.range && hasTarget) {
              e.state[i] = 1;
              e.timer[i] = behavior.windup;
              e.facing[i] = datan2(diry, dirx);
            }
            break;
          case 1:
            if (e.timer[i] <= 0 && hasTarget) {
              const pl = w.players[np.idx];
              e.leapX[i] = pl.x;
              e.leapY[i] = pl.y;
              e.state[i] = 2;
              e.timer[i] = behavior.airTime;
              e.contactCd[i] = behavior.airTime; // no contact mid-air
            }
            break;
          case 2: {
            const k = Math.max(0, e.timer[i] / behavior.airTime); // 1 → 0
            const ldx = wrapDelta(e.leapX[i] - e.x[i], half);
            const ldy = wrapDelta(e.leapY[i] - e.y[i], half);
            const remain = Math.max(dt, e.timer[i]);
            vx = ldx / remain;
            vy = ldy / remain;
            e.z[i] = 4 * k * (1 - k) * 2.5; // parabolic arc, apex 2.5 u
            if (e.timer[i] <= 0) {
              e.z[i] = 0;
              e.state[i] = 3;
              e.timer[i] = behavior.cooldown;
              if (behavior.landPattern) {
                const idx = ctx.reg.patternIndex.get(behavior.landPattern);
                if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY);
              }
              ctx.events.push({ type: 'shake', strength: 0.25 });
              ctx.events.push({ type: 'sfx', id: 'stomp', x: e.x[i], y: e.y[i] });
            }
            break;
          }
          default:
            vx = dirx * spd * 0.4;
            vy = diry * spd * 0.4;
            if (e.timer[i] <= 0) e.state[i] = 0;
        }
        break;
      }
      case 'slide': {
        // state 0 = approach, 1 = windup (stands still, faces you), 2 = skidding, 3 = cooldown
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd;
            vy = diry * spd;
            if (dist < behavior.range && hasTarget) {
              e.state[i] = 1;
              e.timer[i] = behavior.windup;
              e.facing[i] = datan2(diry, dirx);
            }
            break;
          case 1:
            e.facing[i] = datan2(diry, dirx);
            if (e.timer[i] <= 0) {
              e.state[i] = 2;
              e.timer[i] = 3;
              e.momX[i] = dcos(e.facing[i]) * behavior.impulse;
              e.momY[i] = dsin(e.facing[i]) * behavior.impulse;
              ctx.events.push({ type: 'sfx', id: 'slide', x: e.x[i], y: e.y[i] });
            }
            break;
          case 2: {
            // no steering: it skids past you and has to come back around
            const decay = Math.max(0, 1 - behavior.friction * dt);
            e.momX[i] *= decay;
            e.momY[i] *= decay;
            vx = e.momX[i];
            vy = e.momY[i];
            if (dhypot(vx, vy) < 1 || e.timer[i] <= 0) {
              e.state[i] = 3;
              e.timer[i] = behavior.cooldown;
            }
            break;
          }
          default:
            vx = dirx * spd * 0.35;
            vy = diry * spd * 0.35;
            if (e.timer[i] <= 0) e.state[i] = 0;
        }
        break;
      }
      case 'pack': {
        // holds at range until enough packmates are close, then the whole pack commits at once
        let mates = 0;
        const pr2 = behavior.radius * behavior.radius;
        for (let j = 1; j < high && mates < behavior.commitCount; j++) {
          if (j === i || !e.pool.alive[j] || e.isBoss[j] || e.def[j] !== e.def[i]) continue;
          const dx = wrapDelta(e.x[j] - e.x[i], half);
          const dy = wrapDelta(e.y[j] - e.y[i], half);
          if (dx * dx + dy * dy < pr2) mates++;
        }
        const committed = mates >= behavior.commitCount;
        e.state[i] = committed ? 1 : 0;
        if (committed) {
          vx = dirx * spd * behavior.commitSpeedMult;
          vy = diry * spd * behavior.commitSpeedMult;
        } else {
          const want = behavior.distance;
          const f = dist > want + 1.5 ? 1 : dist < want - 1.5 ? -1 : 0;
          vx = (dirx * f - diry * 0.5) * spd;
          vy = (diry * f + dirx * 0.5) * spd;
        }
        break;
      }
      case 'ambush': {
        // buried trap: untargetable and still until you walk close, then it erupts
        if (e.state[i] === 0) {
          e.z[i] = -1;
          if (hasTarget && dist < behavior.triggerRange) {
            e.state[i] = 1;
            e.timer[i] = behavior.riseTime;
            e.hitFlash[i] = behavior.riseTime;
            e.contactCd[i] = behavior.riseTime + 0.2;
            ctx.events.push({ type: 'sfx', id: 'emerge', x: e.x[i], y: e.y[i] });
          }
        } else if (e.state[i] === 1) {
          e.timer[i] -= dt;
          e.z[i] = -Math.max(0, e.timer[i] / behavior.riseTime);
          if (e.timer[i] <= 0) {
            e.state[i] = 2;
            e.z[i] = 0;
          }
        } else {
          e.z[i] = 0;
          vx = dirx * spd;
          vy = diry * spd;
        }
        break;
      }
      case 'shieldWall': {
        // slow advance, always facing you; frontal hits are absorbed (see collision.ts)
        vx = dirx * spd * behavior.speedMult;
        vy = diry * spd * behavior.speedMult;
        if (hasTarget) e.facing[i] = datan2(diry, dirx);
        break;
      }
      case 'deploy': {
        // marches into position then roots as a turret; re-deploys if you walk away
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd;
            vy = diry * spd;
            if (dist < behavior.range && hasTarget) {
              e.state[i] = 1;
              e.timer[i] = behavior.deployTime;
              e.facing[i] = datan2(diry, dirx);
            }
            break;
          case 1: // planting (telegraph)
            e.hitFlash[i] = Math.max(e.hitFlash[i], 0.1);
            if (e.timer[i] <= 0) e.state[i] = 2;
            break;
          default: // deployed turret
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (dist > behavior.undeployRange) e.state[i] = 0;
        }
        break;
      }
      case 'march': {
        // lockstep: advances in bursts with hard stops between them
        e.timer[i] -= dt;
        if (e.timer[i] <= 0) {
          e.state[i] = e.state[i] === 0 ? 1 : 0;
          e.timer[i] = e.state[i] === 0 ? behavior.stepTime : behavior.pauseTime;
        }
        if (e.state[i] === 0) {
          vx = dirx * spd * behavior.speedMult;
          vy = diry * spd * behavior.speedMult;
        } else if (hasTarget) e.facing[i] = datan2(diry, dirx);
        break;
      }
      case 'siege': {
        // 0 = approach, 1 = planting (telegraph), 2 = planted turret, 3 = backing off (min-range breach)
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd;
            vy = diry * spd;
            if (hasTarget && dist <= behavior.settleRange) {
              e.state[i] = 1;
              e.timer[i] = behavior.settleTime;
              e.facing[i] = datan2(diry, dirx);
            }
            break;
          case 1: // planting (telegraph)
            e.hitFlash[i] = Math.max(e.hitFlash[i], 0.1);
            if (e.timer[i] <= 0) e.state[i] = 2;
            break;
          case 2: // planted turret — fires normally, but must break off if a player gets too close
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (hasTarget && dist < behavior.minRange) e.state[i] = 3;
            break;
          default: { // 3: backing off — move straight away from the player, holding fire
            e.patternCd[i] = Math.max(e.patternCd[i], 0.5);
            if (hasTarget) {
              vx = -dirx * spd * behavior.backSpeedMult;
              vy = -diry * spd * behavior.backSpeedMult;
              e.facing[i] = datan2(diry, dirx);
            }
            if (!hasTarget || dist >= behavior.minRange + 2) {
              e.state[i] = 1;
              e.timer[i] = behavior.settleTime;
            }
            break;
          }
        }
        break;
      }
      case 'ram': {
        // 0 = aim (track + creep in), 1 = heave (windup, stand still, telegraph), 2 = run (locked direction, ramping speed)
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0: // aim
            vx = dirx * spd * 0.4;
            vy = diry * spd * 0.4;
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (e.timer[i] <= 0) {
              e.state[i] = 1;
              e.timer[i] = behavior.windup;
            }
            break;
          case 1: // heave — stand still; the last facing update before this ends is what the run locks onto
            vx = 0;
            vy = 0;
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (e.timer[i] <= 0) {
              e.state[i] = 2;
              e.timer[i] = 0; // now counts UP: elapsed run time
            }
            break;
          default: { // 2: run — no steering, speed ramps with elapsed run time
            const mult = Math.min(behavior.maxSpeedMult, 1 + e.timer[i] * behavior.rampPerSec);
            vx = dcos(e.facing[i]) * spd * mult;
            vy = dsin(e.facing[i]) * spd * mult;
            e.timer[i] += dt;
            if (e.timer[i] >= behavior.runTime) {
              e.state[i] = 0;
              e.timer[i] = behavior.aimTime;
            }
            break;
          }
        }
        break;
      }
      case 'flyover': {
        // 0 = loiter (hold standoff), 1 = klaxon windup (locks facing), 2 = run (climb + bomb, no steering),
        // 3 = descend (no steering). specialT is repurposed as the airborne bomb-drop countdown.
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0: { // loiter: hold at standoff like keepDistance
            const want = behavior.standoff;
            let f = 0;
            if (dist > want + 1.5) f = 1;
            else if (dist < want - 1.5) f = -1;
            vx = (dirx * f - diry * 0.15) * spd;
            vy = (diry * f + dirx * 0.15) * spd;
            e.z[i] = 0;
            if (e.timer[i] <= 0 && hasTarget) {
              e.state[i] = 1;
              e.timer[i] = behavior.windup;
              ctx.events.push({ type: 'sfx', id: 'klaxon', x: e.x[i], y: e.y[i] });
              ctx.events.push({ type: 'shake', strength: 0.2 });
            }
            break;
          }
          case 1: // klaxon windup: stand still, lock the bombing lane onto wherever the player is when it ends
            vx = 0;
            vy = 0;
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (e.timer[i] <= 0) {
              e.state[i] = 2;
              e.timer[i] = behavior.runTime;
              e.specialT[i] = behavior.bombInterval;
            }
            break;
          case 2: { // run: climb, dead-straight along the locked facing, dropping bombs on a timer
            e.z[i] = Math.min(behavior.height, e.z[i] + (behavior.height / behavior.climbTime) * dt);
            vx = dcos(e.facing[i]) * spd * behavior.runSpeedMult;
            vy = dsin(e.facing[i]) * spd * behavior.runSpeedMult;
            e.patternCd[i] = Math.max(e.patternCd[i], 0.5); // no self-fire while airborne
            e.specialT[i] -= dt;
            if (e.specialT[i] <= 0) {
              e.specialT[i] = behavior.bombInterval;
              const idx = ctx.reg.patternIndex.get(behavior.bombPattern);
              if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY);
              ctx.events.push({ type: 'sfx', id: 'bombdrop', x: e.x[i], y: e.y[i] });
            }
            if (e.timer[i] <= 0) {
              e.state[i] = 3;
              e.timer[i] = behavior.climbTime;
            }
            break;
          }
          default: { // 3: descend, same locked facing, no steering
            vx = dcos(e.facing[i]) * spd * behavior.runSpeedMult * 0.5;
            vy = dsin(e.facing[i]) * spd * behavior.runSpeedMult * 0.5;
            e.z[i] = Math.max(0, e.z[i] - (behavior.height / behavior.climbTime) * dt);
            if (e.timer[i] <= 0) {
              e.z[i] = 0;
              e.state[i] = 0;
              e.timer[i] = behavior.loiterTime;
            }
            break;
          }
        }
        break;
      }
      case 'assemble': {
        // 0 = creep toward the player, 1 = stamp windup (telegraph, then rings out reinforcements)
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd * behavior.driftSpeedMult;
            vy = diry * spd * behavior.driftSpeedMult;
            if (e.timer[i] <= 0 && hasTarget && dist < behavior.range) {
              e.state[i] = 1;
              e.timer[i] = behavior.windup;
              ctx.events.push({ type: 'sfx', id: 'stamp', x: e.x[i], y: e.y[i] });
            }
            break;
          default: { // stamp windup: stand still, telegraph, then ring out reinforcements
            vx = 0;
            vy = 0;
            if (e.timer[i] <= 0) {
              for (let k = 0; k < behavior.count; k++) {
                const a = (k / behavior.count) * TAU;
                const sx = e.x[i] + dcos(a) * behavior.spread;
                const sy = e.y[i] + dsin(a) * behavior.spread;
                const s = spawnEnemy(ctx, behavior.enemy, sx, sy, false, e.objectiveWave[i] === 1);
                if (s !== 0) {
                  e.hitFlash[s] = 0.25; // stamp flash doubles as the telegraph
                  e.contactCd[s] = 0.4; // no instant contact damage on emerge
                }
              }
              ctx.events.push({ type: 'shake', strength: 0.2 });
              e.state[i] = 0;
              e.timer[i] = behavior.interval;
            }
            break;
          }
        }
        break;
      }
      case 'lockon': {
        // 0 = reposition, 2 = track (live facing, ping telegraph), 1 = locked (frozen facing, fires on expiry), 3 = cooldown reposition
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd * behavior.driftSpeedMult;
            vy = diry * spd * behavior.driftSpeedMult;
            if (hasTarget && dist < behavior.range) {
              e.state[i] = 2;
              e.timer[i] = behavior.trackTime;
              ctx.events.push({ type: 'sfx', id: 'lockping', x: e.x[i], y: e.y[i] });
            }
            break;
          case 2: // track: live facing, telegraph ping already fired on entry above
            vx = 0;
            vy = 0;
            if (hasTarget) e.facing[i] = datan2(diry, dirx);
            if (e.timer[i] <= 0) {
              e.state[i] = 1;
              e.timer[i] = behavior.lockTime;
              ctx.events.push({ type: 'sfx', id: 'lockon', x: e.x[i], y: e.y[i] });
            }
            break;
          case 1: // locked: facing is frozen — never updated from the live target here
            vx = 0;
            vy = 0;
            if (e.timer[i] <= 0) {
              const idx = ctx.reg.patternIndex.get(behavior.lockPattern);
              if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY, e.facing[i]);
              ctx.events.push({ type: 'sfx', id: 'lance', x: e.x[i], y: e.y[i] });
              e.state[i] = 3;
              e.timer[i] = behavior.cooldown;
            }
            break;
          default: // 3: cooldown — reposition just like state 0
            vx = dirx * spd * behavior.driftSpeedMult;
            vy = diry * spd * behavior.driftSpeedMult;
            if (e.timer[i] <= 0) e.state[i] = 0;
        }
        break;
      }
      case 'gridstep': {
        // pure axis-aligned pursuit: drives on X xor Y, never diagonally
        e.timer[i] -= dt;
        const gdx = np.dx;
        const gdy = np.dy;
        const gax = Math.abs(gdx);
        const gay = Math.abs(gdy);
        if (e.timer[i] <= 0 || (e.state[i] === 0 && gax < behavior.laneWidth) || (e.state[i] === 1 && gay < behavior.laneWidth)) {
          e.state[i] = gax >= gay ? 0 : 1;
          e.timer[i] = behavior.switchTime;
        }
        if (e.state[i] === 0) {
          vx = Math.sign(gdx) * spd * behavior.speedMult;
          vy = 0;
        } else {
          vx = 0;
          vy = Math.sign(gdy) * spd * behavior.speedMult;
        }
        break;
      }
      case 'lag': {
        // hunts the player's position from `lagTime` s ago, not their live position
        e.timer[i] -= dt;
        if (e.timer[i] <= 0 && hasTarget) {
          e.timer[i] = behavior.lagTime;
          const pl = w.players[np.idx];
          e.leapX[i] = pl.x;
          e.leapY[i] = pl.y;
          e.state[i] = 0;
          e.hitFlash[i] = 0.12; // brief flash reads as "it just saw you"
          ctx.events.push({ type: 'sfx', id: 'echoping', x: e.x[i], y: e.y[i] });
        }
        if (e.state[i] === 0) {
          const ldx = wrapDelta(e.leapX[i] - e.x[i], half);
          const ldy = wrapDelta(e.leapY[i] - e.y[i], half);
          const ld = Math.sqrt(ldx * ldx + ldy * ldy);
          if (ld < behavior.arriveRadius) {
            e.state[i] = 1; // arrived — wait for the next tick of the lag clock
          } else if (ld > 1e-4) {
            vx = (ldx / ld) * spd * behavior.speedMult;
            vy = (ldy / ld) * spd * behavior.speedMult;
          }
        }
        break;
      }
      case 'unwind': {
        // 0 = advance (remembers its start point), 1 = frozen snap telegraph, 2 = recover (half-speed chase)
        e.timer[i] -= dt;
        switch (e.state[i]) {
          case 0:
            vx = dirx * spd * behavior.speedMult;
            vy = diry * spd * behavior.speedMult;
            if (e.timer[i] <= 0) {
              e.state[i] = 1;
              e.timer[i] = behavior.snapTelegraph;
              ctx.events.push({ type: 'sfx', id: 'unwind', x: e.x[i], y: e.y[i] });
            }
            break;
          case 1: // frozen telegraph: rooted, guns cold (see isWindingUp in instanced.ts)
            vx = 0;
            vy = 0;
            e.patternCd[i] = Math.max(e.patternCd[i], behavior.snapTelegraph);
            if (e.timer[i] <= 0) {
              const sdx = wrapDelta(e.leapX[i] - e.x[i], half);
              const sdy = wrapDelta(e.leapY[i] - e.y[i], half);
              for (let k = 0; k < behavior.scarCount; k++) {
                const f = (k + 1) / (behavior.scarCount + 1);
                const hx = wrapCoord(e.x[i] + sdx * f, half);
                const hy = wrapCoord(e.y[i] + sdy * f, half);
                const hi = spawnHazard(ctx, hx, hy, behavior.scarRadius, behavior.scarTelegraph + behavior.scarDuration, behavior.scarDps, 0xff8a3c, SIDE_ENEMY);
                if (hi !== 0) w.hazards.armT[hi] = behavior.scarTelegraph;
              }
              e.x[i] = e.leapX[i];
              e.y[i] = e.leapY[i];
              e.px[i] = e.x[i];
              e.py[i] = e.y[i];
              e.invuln[i] = 0.25; // brief arrival grace, matching blink's teleport convention
              e.contactCd[i] = 0.4; // no instant contact damage on arrival, matching burrow's emerge
              ctx.events.push({ type: 'sfx', id: 'snapback', x: e.x[i], y: e.y[i] });
              ctx.events.push({ type: 'shake', strength: 0.15 });
              e.state[i] = 2;
              e.timer[i] = behavior.recoverTime;
            }
            break;
          default: // 2: recover — half-speed chase, patterns fire normally
            vx = dirx * spd * 0.5;
            vy = diry * spd * 0.5;
            if (e.timer[i] <= 0) {
              e.state[i] = 0;
              e.timer[i] = behavior.advanceTime;
              e.leapX[i] = e.x[i]; // fresh snap-back point for the next advance
              e.leapY[i] = e.y[i];
            }
            break;
        }
        break;
      }
    }

    // boss signature mechanic (may add velocity: dash)
    if (phase?.special) {
      const sp = phase.special;
      switch (sp.type) {
        case 'dash': {
          e.specialT[i] -= dt;
          if (e.specialActive[i] > 0) {
            e.specialActive[i] -= dt;
            if (e.state[i] === 1) {
              // windup: stand still, track target
              vx = 0;
              vy = 0;
              e.facing[i] = datan2(diry, dirx);
              if (e.specialActive[i] <= 0) {
                e.state[i] = 2;
                e.specialActive[i] = sp.duration;
              }
            } else {
              vx = dcos(e.facing[i]) * spd * sp.speedMult;
              vy = dsin(e.facing[i]) * spd * sp.speedMult;
              if (e.specialActive[i] <= 0) e.state[i] = 0;
            }
          } else if (e.specialT[i] <= 0 && hasTarget && e.invuln[i] <= 0) {
            e.specialT[i] = sp.interval;
            e.specialActive[i] = sp.windup;
            e.state[i] = 1;
            ctx.events.push({ type: 'sfx', id: 'roar', x: e.x[i], y: e.y[i] });
          }
          break;
        }
        case 'pull': {
          e.specialT[i] -= dt;
          if (e.specialActive[i] > 0) {
            e.specialActive[i] -= dt;
            const r2 = sp.radius * sp.radius;
            for (let pi = 0; pi < w.players.length; pi++) {
              const pl = w.players[pi];
              if (!pl.alive) continue;
              const dx = wrapDelta(e.x[i] - pl.x, half);
              const dy = wrapDelta(e.y[i] - pl.y, half);
              const d2 = dx * dx + dy * dy;
              if (d2 > r2 || d2 < 4) continue;
              const d = Math.sqrt(d2);
              pl.x = wrapCoord(pl.x + (dx / d) * sp.strength * dt, half);
              pl.y = wrapCoord(pl.y + (dy / d) * sp.strength * dt, half);
            }
          } else if (e.specialT[i] <= 0 && e.invuln[i] <= 0) {
            e.specialT[i] = sp.interval;
            e.specialActive[i] = sp.duration;
            ctx.events.push({ type: 'shake', strength: 0.3 });
            ctx.events.push({ type: 'sfx', id: 'pull', x: e.x[i], y: e.y[i] });
          }
          break;
        }
        case 'shield':
          // invulnerable while shield adds live (count maintained by killEnemy)
          if (e.shieldAdds[i] > 0 && e.invuln[i] < dt * 2) e.invuln[i] = dt * 2;
          break;
        case 'quake': {
          e.specialT[i] -= dt;
          if (e.specialT[i] <= 0 && e.invuln[i] <= 0) {
            e.specialT[i] = sp.interval;
            for (let k = 0; k < sp.count; k++) {
              const pl = w.players[k % w.players.length];
              const a = rng.range(0, TAU);
              const r = rng.range(0, sp.spread);
              const hx = wrapCoord((pl.alive ? pl.x : e.x[i]) + dcos(a) * r, half);
              const hy = wrapCoord((pl.alive ? pl.y : e.y[i]) + dsin(a) * r, half);
              const hi = spawnHazard(ctx, hx, hy, sp.radius, sp.telegraph + sp.duration, sp.dps, 0xff8a3c, SIDE_ENEMY);
              if (hi !== 0) w.hazards.armT[hi] = sp.telegraph;
            }
            ctx.events.push({ type: 'shake', strength: 0.35 });
            ctx.events.push({ type: 'sfx', id: 'quake', x: e.x[i], y: e.y[i] });
          }
          break;
        }
        case 'trail':
          break;
        case 'flight': {
          switch (e.state[i]) {
            case 0: // grounded — the phase's own (stateless) behavior runs untouched above
              e.specialT[i] -= dt;
              if (e.specialT[i] <= 0 && e.invuln[i] <= 0) {
                e.state[i] = 1;
                e.specialActive[i] = sp.windup;
                ctx.events.push({ type: 'shake', strength: 0.3 });
                ctx.events.push({ type: 'sfx', id: 'wingbeat', x: e.x[i], y: e.y[i] });
              }
              break;
            case 1: // wing-beat windup: stand still, track target, telegraph (see isWindingUp in instanced.ts)
              vx = 0;
              vy = 0;
              if (hasTarget) e.facing[i] = datan2(diry, dirx);
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                e.state[i] = 2;
                e.specialActive[i] = sp.airTime;
                e.trailT[i] = sp.bombInterval; // repurposed as the airborne bomb-drop countdown
              }
              break;
            case 2: { // airborne: untargetable (z-height guards in collision.ts/player.ts), bombs, chases faster
              e.z[i] = Math.min(sp.height, e.z[i] + (sp.height / 0.35) * dt);
              vx = dirx * spd * 1.8;
              vy = diry * spd * 1.8;
              e.specialActive[i] -= dt;
              e.trailT[i] -= dt;
              if (e.trailT[i] <= 0) {
                e.trailT[i] = sp.bombInterval;
                const idx = ctx.reg.patternIndex.get(sp.bombPattern);
                if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY);
              }
              if (e.specialActive[i] <= 0) {
                e.state[i] = 3;
                e.specialActive[i] = sp.landTelegraph;
                if (hasTarget) {
                  const a = rng.range(0, TAU);
                  const pl = w.players[np.idx];
                  e.x[i] = wrapCoord(pl.x + dcos(a) * sp.landRange, half);
                  e.y[i] = wrapCoord(pl.y + dsin(a) * sp.landRange, half);
                  e.px[i] = e.x[i];
                  e.py[i] = e.y[i];
                }
                const hi = spawnHazard(ctx, e.x[i], e.y[i], sp.landRadius, sp.landTelegraph + 0.8, sp.landDps, 0xff8a3c, SIDE_ENEMY);
                if (hi !== 0) w.hazards.armT[hi] = sp.landTelegraph;
                ctx.events.push({ type: 'sfx', id: 'stomp', x: e.x[i], y: e.y[i] });
              }
              break;
            }
            default: { // 3: slam telegraph — descend to ground over landTelegraph, then fire landPattern
              const k = Math.max(0, e.specialActive[i] / sp.landTelegraph);
              e.z[i] = sp.height * k;
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                e.z[i] = 0;
                const idx = ctx.reg.patternIndex.get(sp.landPattern);
                if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY);
                ctx.events.push({ type: 'shake', strength: 0.5 });
                e.state[i] = 0;
                e.specialT[i] = sp.interval;
              }
              break;
            }
          }
          break;
        }
        case 'overheat': {
          switch (e.state[i]) {
            case 0: // running — the phase's own (stateless) behavior runs untouched above
              e.specialT[i] -= dt;
              if (e.specialT[i] <= 0 && e.invuln[i] <= 0) {
                e.state[i] = 1;
                e.specialActive[i] = sp.windup;
                ctx.events.push({ type: 'sfx', id: 'klaxon', x: e.x[i], y: e.y[i] });
                ctx.events.push({ type: 'shake', strength: 0.3 });
              }
              break;
            case 1: // pressure windup: stand still, track target, hold fire through the coming vent
              vx = 0;
              vy = 0;
              if (hasTarget) e.facing[i] = datan2(diry, dirx);
              e.patternCd[i] = Math.max(e.patternCd[i], sp.ventTime + sp.windup);
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                e.state[i] = 2;
                e.specialActive[i] = sp.ventTime;
                e.trailT[i] = 0; // fire the first ring immediately
              }
              break;
            case 2: { // vent: rooted, spits rings of armed steam discs on a timer
              vx = 0;
              vy = 0;
              e.specialActive[i] -= dt;
              e.trailT[i] -= dt;
              if (e.trailT[i] <= 0) {
                e.trailT[i] = sp.ringInterval;
                const ringIndex = Math.floor((sp.ventTime - e.specialActive[i]) / sp.ringInterval);
                const r = sp.ringStep * (ringIndex + 1);
                const off = (ringIndex % 2) * (Math.PI / sp.ringCount);
                for (let k = 0; k < sp.ringCount; k++) {
                  const a = off + (k * (2 * Math.PI)) / sp.ringCount;
                  const hx = wrapCoord(e.x[i] + dcos(a) * r, half);
                  const hy = wrapCoord(e.y[i] + dsin(a) * r, half);
                  const hi = spawnHazard(ctx, hx, hy, sp.ringRadius, sp.ringTelegraph + sp.ringDuration, sp.ringDps, 0xff6a20, SIDE_ENEMY);
                  if (hi !== 0) w.hazards.armT[hi] = sp.ringTelegraph;
                }
                ctx.events.push({ type: 'sfx', id: 'steamvent', x: e.x[i], y: e.y[i] });
              }
              if (e.specialActive[i] <= 0) {
                if (sp.purgeTime > 0) {
                  e.state[i] = 3;
                  e.specialActive[i] = sp.purgeTime;
                  e.patternCd[i] = 0; // guns immediately hot
                } else {
                  e.state[i] = 0;
                  e.specialT[i] = sp.interval;
                }
              }
              break;
            }
            default: // 3: purge — the phase behavior already computed vx/vy above; just scale it
              vx *= sp.purgeSpeedMult;
              vy *= sp.purgeSpeedMult;
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                e.state[i] = 0;
                e.specialT[i] = sp.interval;
              }
              break;
          }
          break;
        }
        case 'subsystems': {
          // 0 = armed (pods live, phase behavior drives movement untouched), 2 = exposed (all pods dead,
          // waiting out `redeploy`), 1 = deploying (rooted, guns cold, stamping a fresh rack)
          switch (e.state[i]) {
            case 0: {
              e.specialT[i] -= dt;
              if (e.specialT[i] <= 0) {
                e.specialT[i] = sp.podInterval;
                const pidx = ctx.reg.patternIndex.get(sp.podPattern);
                if (pidx !== undefined) {
                  for (let i2 = 1; i2 < high; i2++) {
                    if (e.pool.alive[i2] && e.protects[i2] === i) firePatternAt(ctx, pidx, e.x[i2], e.y[i2], SIDE_ENEMY);
                  }
                }
              }
              if (e.shieldAdds[i] === 0) {
                // last pod just died — fire the readable hull burst once, then either pause exposed or
                // (redeploy === 0) restamp immediately
                const eidx = ctx.reg.patternIndex.get(sp.exposedPattern);
                if (eidx !== undefined) firePatternAt(ctx, eidx, e.x[i], e.y[i], SIDE_ENEMY);
                ctx.events.push({ type: 'sfx', id: 'systemsfail', x: e.x[i], y: e.y[i] });
                ctx.events.push({ type: 'shake', strength: 0.4 });
                if (sp.redeploy > 0) {
                  e.state[i] = 2;
                  e.specialActive[i] = sp.redeploy;
                } else {
                  e.state[i] = 1;
                  e.timer[i] = sp.deployTelegraph;
                  ctx.events.push({ type: 'sfx', id: 'podeploy', x: e.x[i], y: e.y[i] });
                }
              }
              break;
            }
            case 2: // exposed: normal behavior continues driving movement (only reached when redeploy > 0)
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                e.state[i] = 1;
                e.timer[i] = sp.deployTelegraph;
                ctx.events.push({ type: 'sfx', id: 'podeploy', x: e.x[i], y: e.y[i] });
              }
              break;
            default: { // 1: deploying — rooted, guns cold, red-pulse telegraph (see isWindingUp in instanced.ts)
              vx = 0;
              vy = 0;
              e.patternCd[i] = Math.max(e.patternCd[i], sp.deployTelegraph);
              e.timer[i] -= dt;
              if (e.timer[i] <= 0) {
                spawnPodRack(ctx, i, sp);
                e.state[i] = 0;
              }
              break;
            }
          }
          break;
        }
        case 'devour': {
          // 0 = running (phase's own behavior drives movement untouched), 1 = windup (rooted, guns cold)
          switch (e.state[i]) {
            case 0:
              e.specialT[i] -= dt;
              if (e.specialT[i] <= 0 && e.invuln[i] <= 0) {
                if (hasTarget) {
                  const pl = w.players[np.idx];
                  e.leapX[i] = pl.x;
                  e.leapY[i] = pl.y;
                } else {
                  e.leapX[i] = e.x[i];
                  e.leapY[i] = e.y[i];
                }
                const hi = spawnHazard(ctx, e.leapX[i], e.leapY[i], sp.radius, sp.windup + sp.duration, sp.dps, 0xff8a3c, SIDE_ENEMY);
                if (hi !== 0) w.hazards.armT[hi] = sp.windup;
                e.specialT[i] = sp.interval;
                e.specialActive[i] = sp.windup;
                e.state[i] = 1;
                ctx.events.push({ type: 'sfx', id: 'devour', x: e.x[i], y: e.y[i] });
                ctx.events.push({ type: 'shake', strength: 0.3 });
              }
              break;
            default: { // 1: windup, rooted — red-pulse telegraph (see isWindingUp in instanced.ts)
              vx = 0;
              vy = 0;
              e.patternCd[i] = Math.max(e.patternCd[i], e.specialActive[i]);
              e.specialActive[i] -= dt;
              if (e.specialActive[i] <= 0) {
                const idx = ctx.reg.patternIndex.get(sp.recallPattern);
                if (idx !== undefined) firePatternAt(ctx, idx, e.leapX[i], e.leapY[i], SIDE_ENEMY);
                for (let k = 0; k < sp.echoCount; k++) {
                  const a = (k / sp.echoCount) * TAU;
                  const sx = wrapCoord(e.leapX[i] + dcos(a) * sp.echoSpread, half);
                  const sy = wrapCoord(e.leapY[i] + dsin(a) * sp.echoSpread, half);
                  const s = spawnEnemy(ctx, sp.echoEnemy, sx, sy);
                  if (s !== 0) {
                    e.hitFlash[s] = 0.25;
                    e.contactCd[s] = 0.4; // no instant contact damage on emerge
                  }
                }
                ctx.events.push({ type: 'sfx', id: 'unmade', x: e.leapX[i], y: e.leapY[i] });
                ctx.events.push({ type: 'shake', strength: 0.45 });
                e.state[i] = 0;
              }
              break;
            }
          }
          break;
        }
      }
    }

    // separation from neighbours (cheap, grid-based)
    if (!e.isBoss[i] && e.z[i] < 0.5) {
      const r = e.radius[i];
      let sx = 0;
      let sy = 0;
      let n = 0;
      const g = w.grid;
      g.prepare(e.x[i], e.y[i], r * 2);
      for (let cy = g.qy0; cy <= g.qy1 && n < 6; cy++) {
        for (let cx = g.qx0; cx <= g.qx1 && n < 6; cx++) {
          const c = g.wrapRow(cy) * g.cols + g.wrapCol(cx);
          const s = g.cellStart[c];
          const en = g.cellStart[c + 1];
          for (let k = s; k < en; k++) {
            const j = g.items[k];
            if (j === i || e.z[j] > 0.5) continue;
            const dx = wrapDelta(e.x[i] - e.x[j], half);
            const dy = wrapDelta(e.y[i] - e.y[j], half);
            const rr = r + e.radius[j];
            const d2 = dx * dx + dy * dy;
            if (d2 < rr * rr && d2 > 1e-6) {
              const d = Math.sqrt(d2);
              const push = (rr - d) / rr;
              sx += (dx / d) * push;
              sy += (dy / d) * push;
              if (++n >= 6) break;
            }
          }
        }
      }
      if (n > 0) {
        vx += sx * 6;
        vy += sy * 6;
      }
    }

    e.vx[i] = vx;
    e.vy[i] = vy;
    const nx = wrapCoord(e.x[i] + vx * dt, half);
    const ny = wrapCoord(e.y[i] + vy * dt, half);
    if (Math.abs(nx - e.x[i]) > half) e.px[i] = nx - vx * dt;
    if (Math.abs(ny - e.y[i]) > half) e.py[i] = ny - vy * dt;
    e.x[i] = nx;
    e.y[i] = ny;
    if (vx !== 0 || vy !== 0) {
      if (behavior.type !== 'charge' && behavior.type !== 'dive' && behavior.type !== 'leap' && behavior.type !== 'slide' && behavior.type !== 'shieldWall' && behavior.type !== 'deploy' && behavior.type !== 'siege' && behavior.type !== 'ram' && behavior.type !== 'flyover' && behavior.type !== 'lockon') e.facing[i] = datan2(vy, vx);
    }

    // hazard trail (enemy def or boss special)
    const trail = def?.trail ?? (phase?.special?.type === 'trail' ? phase.special.trail : undefined);
    if (trail && Math.abs(e.z[i]) < 0.5 && (vx !== 0 || vy !== 0)) {
      e.trailT[i] -= dt;
      if (e.trailT[i] <= 0) {
        e.trailT[i] = trail.interval;
        spawnHazard(ctx, e.x[i], e.y[i], trail.radius, trail.duration, trail.dps, trail.color ?? 0xff5577, SIDE_ENEMY);
      }
    }

    // pattern runner
    const fireRange = def?.fireRange ?? 30;
    const patterns = phase ? phase.patterns : def?.patterns;
    let cooldown = phase ? phase.patternCooldown : (def?.patternCooldown ?? 2);
    if (e.affixes[i] & AFFIX_BITS.hasted) cooldown *= AFFIX_HASTE_COOLDOWN_MULT;
    if (patterns && patterns.length > 0 && hasTarget && e.invuln[i] <= 0) {
      stepPattern(ctx, i, patterns, cooldown, dist <= fireRange, datan2(np.dy, np.dx), dt);
    }

    // boss phase logic
    if (e.isBoss[i] && ctx.boss) {
      updateBossPhase(ctx, i, dt);
    }
  }
  if ((w.tick & 31) === 0) e.pool.compactHigh();
}

function stepPattern(ctx: SimCtx, i: number, patterns: string[], cooldown: number, inRange: boolean, aim: number, dt: number): void {
  const e = ctx.w.enemies;
  const reg = ctx.reg;
  if (e.patternIdx[i] < 0) {
    if (e.patternCd[i] > 0) {
      e.patternCd[i] -= dt;
      return;
    }
    if (!inRange) return;
    const slot = e.patternSlot[i] % patterns.length;
    const idx = reg.patternIndex.get(patterns[slot]);
    e.patternSlot[i] = (slot + 1) % patterns.length;
    if (idx === undefined) return;
    e.patternIdx[i] = idx;
    e.patternT[i] = 0;
    const p = reg.patternList[idx];
    const base = i * MAX_EMITTERS;
    for (let k = 0; k < p.emitters.length && k < MAX_EMITTERS; k++) {
      e.emitNext[base + k] = p.emitters[k].start ?? 0;
      e.emitBursts[base + k] = 0;
    }
  }
  const idx = e.patternIdx[i];
  const p = reg.patternList[idx];
  e.patternT[i] += dt;
  const t = e.patternT[i];
  const base = i * MAX_EMITTERS;
  const dmgMult = ctx.scale.damage * (e.elite[i] ? ELITE_DMG_MULT : 1);
  for (let k = 0; k < p.emitters.length && k < MAX_EMITTERS; k++) {
    const em = p.emitters[k];
    let guard = 0;
    while ((em.bursts === 0 || e.emitBursts[base + k] < em.bursts) && t >= e.emitNext[base + k] && guard++ < 8) {
      const burst = e.emitBursts[base + k];
      ctx._burst.damage = em.bullet.damage * dmgMult;
      if (burst === 0) ctx.events.push({ type: 'enemyFire', x: e.x[i], y: e.y[i], projectile: { color: em.bullet.color, visual: BULLET_VISUALS.indexOf(em.bullet.visual), elements: 0 } });
      emitBurst(ctx._burst, em, e.x[i], e.y[i], aim, burst);
      e.emitBursts[base + k] = burst + 1;
      e.emitNext[base + k] += Math.max(0.016, em.interval);
    }
  }
  if (t >= ctx.patternDurations[idx]) {
    e.patternIdx[i] = -1;
    e.patternCd[i] = cooldown;
  }
}

/** Fire a whole pattern instantly at a position (used for onDeath / onExpire spawns). Ignores timing; fires burst 0 of each emitter.
 * `aimOverride`, when given, is used as the aim direction instead of aiming at the nearest player's live position
 * (e.g. `lockon` firing along its frozen bearing). */
export function firePatternAt(ctx: SimCtx, patternIdx: number, x: number, y: number, side: number, aimOverride?: number): void {
  const p = ctx.reg.patternList[patternIdx];
  if (!p) return;
  const np = nearestPlayer(ctx, x, y);
  const aim = aimOverride !== undefined ? aimOverride : np.idx >= 0 ? datan2(np.dy, np.dx) : 0;
  const b = ctx._burst;
  b.side = side;
  for (const em of p.emitters) {
    b.damage = em.bullet.damage * ctx.scale.damage;
    ctx.events.push({ type: 'enemyFire', x, y, projectile: { color: em.bullet.color, visual: BULLET_VISUALS.indexOf(em.bullet.visual), elements: 0 } });
    emitBurst(b, em, x, y, aim, 0);
  }
  b.side = SIDE_ENEMY;
}

function updateBossPhase(ctx: SimCtx, i: number, dt: number): void {
  const e = ctx.w.enemies;
  const boss = ctx.boss!;
  const frac = e.hp[i] / e.maxHp[i];
  const phases = boss.def.phases;
  let want = e.phase[i];
  for (let k = phases.length - 1; k > e.phase[i]; k--) {
    if (frac <= phases[k].hpBelow) {
      want = k;
      break;
    }
  }
  if (want !== e.phase[i]) {
    e.phase[i] = want;
    e.phaseT[i] = 0;
    e.phaseEntryFired[i] = 0;
    e.patternIdx[i] = -1;
    e.patternCd[i] = 0.5;
    e.summonT[i] = 0;
    e.invuln[i] = phases[want].transition ?? 1.5;
    enterBossPhase(ctx, i, want);
    ctx.events.push({ type: 'bossPhase', phase: want });
    ctx.events.push({ type: 'shake', strength: 0.8 });
  }
  e.phaseT[i] += dt;
  const ph = phases[e.phase[i]];
  // one-shot on-enter pattern fires as the transition telegraph ends
  if (ph.onEnter && e.phaseEntryFired[i] === 0 && e.invuln[i] <= 0 && e.phaseT[i] >= (ph.transition ?? 1.5)) {
    e.phaseEntryFired[i] = 1;
    const idx = ctx.reg.patternIndex.get(ph.onEnter);
    if (idx !== undefined) firePatternAt(ctx, idx, e.x[i], e.y[i], SIDE_ENEMY);
  }
  if (ph.summons) {
    e.summonT[i] -= dt;
    if (e.summonT[i] <= 0) {
      e.summonT[i] = ph.summons.interval;
      spawnGroup(ctx, ph.summons.enemy, ph.summons.count, 'ring', ph.summons.elite);
    }
  }
}

/** Spawn a fresh rack of `subsystems` pods around the boss (used on phase entry and on redeploy). */
function spawnPodRack(ctx: SimCtx, i: number, sp: Extract<BossSpecialDef, { type: 'subsystems' }>): void {
  const e = ctx.w.enemies;
  const half = ctx.w.half;
  for (let k = 0; k < sp.count; k++) {
    const a = (k / sp.count) * TAU;
    const sx = wrapCoord(e.x[i] + dcos(a) * sp.orbitRadius, half);
    const sy = wrapCoord(e.y[i] + dsin(a) * sp.orbitRadius, half);
    const s = spawnEnemy(ctx, sp.enemy, sx, sy);
    if (s !== 0) {
      e.protects[s] = i;
      e.shieldAdds[i]++;
      e.contactCd[s] = 0.4;
      e.facing[s] = a;
    }
  }
}

/** Keeps pods (`e.protects[i2]` pointing at a `subsystems` boss) glued to their boss's rotating rack.
 * Called once per tick right after `updateEnemies` so pods track the boss's just-updated position
 * instead of lagging a frame behind. */
export function updateAttachedPods(ctx: SimCtx, dt: number): void {
  const e = ctx.w.enemies;
  const half = ctx.w.half;
  const high = e.pool.high;
  for (let i2 = 1; i2 < high; i2++) {
    if (!e.pool.alive[i2]) continue;
    const b = e.protects[i2];
    if (b === 0 || !e.pool.alive[b] || !e.isBoss[b] || !ctx.boss || ctx.boss.slot !== b) continue;
    const ph = ctx.boss.def.phases[e.phase[b]];
    if (ph.special?.type !== 'subsystems') continue;
    e.facing[i2] += ph.special.angular * dt;
    e.x[i2] = wrapCoord(e.x[b] + ph.special.orbitRadius * dcos(e.facing[i2]), half);
    e.y[i2] = wrapCoord(e.y[b] + ph.special.orbitRadius * dsin(e.facing[i2]), half);
    e.vx[i2] = 0;
    e.vy[i2] = 0;
  }
}

/** Phase-entry side effects: reset special timers, spawn shield adds. */
function enterBossPhase(ctx: SimCtx, i: number, phase: number, curBossDef = ctx.boss?.def): void {
  const e = ctx.w.enemies;
  const ph = curBossDef?.phases[phase];
  e.state[i] = 0;
  e.specialT[i] = 0;
  e.specialActive[i] = 0;
  e.shieldAdds[i] = 0;
  e.z[i] = 0; // a phase change mid-flight must not permanently strand the boss airborne/untargetable
  if (!ph) return;
  if (ph.special?.type === 'shield') {
    const n = ph.special.count;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * TAU;
      const r = e.radius[i] + 3.5;
      const s = spawnEnemy(ctx, ph.special.enemy, wrapCoord(e.x[i] + dcos(a) * r, ctx.w.half), wrapCoord(e.y[i] + dsin(a) * r, ctx.w.half));
      if (s !== 0) {
        e.protects[s] = i;
        e.shieldAdds[i]++;
      }
    }
  }
  if (ph.special?.type === 'dash') e.specialT[i] = ph.special.interval * 0.5;
  if (ph.special?.type === 'pull') e.specialT[i] = ph.special.interval * 0.5;
  if (ph.special?.type === 'quake') e.specialT[i] = ph.special.interval * 0.4;
  if (ph.special?.type === 'flight') e.specialT[i] = ph.special.interval * 0.5;
  if (ph.special?.type === 'devour') e.specialT[i] = ph.special.interval * 0.5;
  if (ph.special?.type === 'subsystems') {
    // a phase change must not leave the previous phase's rack orphaned (stale HP/count) — sweep any
    // enemy still protecting this boss, then immediately stamp a fresh rack so the boss never starts pod-less.
    for (let i2 = 1; i2 < e.pool.high; i2++) {
      if (e.pool.alive[i2] && e.protects[i2] === i) {
        e.protects[i2] = 0;
        e.pool.release(i2);
      }
    }
    e.shieldAdds[i] = 0;
    e.specialT[i] = ph.special.podInterval * 0.5;
    spawnPodRack(ctx, i, ph.special);
  }
}

/** Spawn a ground hazard disc. */
export function spawnHazard(ctx: SimCtx, x: number, y: number, radius: number, duration: number, dps: number, color: number, side: number): number {
  const h = ctx.w.hazards;
  const hi = h.pool.alloc();
  if (hi === 0) return 0;
  h.x[hi] = x;
  h.y[hi] = y;
  h.radius[hi] = radius;
  h.life[hi] = duration;
  h.maxLife[hi] = duration;
  h.dps[hi] = dps;
  h.color[hi] = color;
  h.side[hi] = side;
  h.armT[hi] = 0;
  return hi;
}

/**
 * Kill an enemy: XP/shard drops, on-death effects, events. `killer` = player index or -1.
 */
export function killEnemy(ctx: SimCtx, i: number, killer: number): void {
  const e = ctx.w.enemies;
  if (!e.pool.alive[i]) return;
  const x = e.x[i];
  const y = e.y[i];
  const isBoss = e.isBoss[i] === 1;
  const def = isBoss ? null : ctx.reg.enemyList[e.def[i]];
  const elite = e.elite[i] === 1;
  const affixMask = e.affixes[i];
  const objectiveWave = e.objectiveWave[i] === 1;
  e.pool.release(i);

  if (isBoss && ctx.boss) {
    const bd = ctx.boss.def;
    ctx.boss = null;
    dropPickup(ctx, x, y, 0, bd.xp * ctx.scale.xp);
    for (let k = 0; k < 6; k++) dropPickup(ctx, x + ctx.rng.range(-2, 2), y + ctx.rng.range(-2, 2), 2, 5);
    dropPickup(ctx, x, y, 1, HEART_HEAL_FRACTION);
    ctx.kills++;
    ctx.bossKills++;
    ctx.events.push({ type: 'bossDeath', defId: bd.id, x, y });
    ctx.events.push({ type: 'shake', strength: 1.2 });
    if (killer >= 0) {
      const player = ctx.w.players[killer];
      player.kills++;
      if (ctx.skillFlags[killer].has('phantom') && player.keystoneWindow > 0) {
        player.dashCdT = 0;
        player.keystoneWindow = 0;
      }
    }
    return;
  }
  if (!def) return;
  ctx.objectiveKill?.(def.id, elite);
  // shield add died → its boss loses one layer
  if (e.protects[i] !== 0) {
    const b = e.protects[i];
    if (e.pool.alive[b] && e.shieldAdds[b] > 0) e.shieldAdds[b]--;
    e.protects[i] = 0;
  }
  const xp = def.xp * ctx.scale.xp * (elite ? ELITE_XP_MULT : 1);
  dropPickup(ctx, x, y, 0, xp);
  if (ctx.rng.chance((def.shardChance ?? 0.02) * (elite ? 5 : 1))) dropPickup(ctx, x, y, 2, elite ? 3 : 1);
  if (ctx.rng.chance(elite ? 0.5 : HEART_DROP_CHANCE)) dropPickup(ctx, x, y, 1, HEART_HEAL_FRACTION);
  if (def.onDeath?.spawn) {
    const s = def.onDeath.spawn;
    for (let k = 0; k < s.count; k++) {
      const a = (k / s.count) * TAU;
      const d = s.spread ?? 1;
      spawnEnemy(ctx, s.enemy, x + dcos(a) * d, y + dsin(a) * d, false, objectiveWave);
    }
  }
  if (def.onDeath?.pattern) {
    const idx = ctx.reg.patternIndex.get(def.onDeath.pattern);
    if (idx !== undefined) firePatternAt(ctx, idx, x, y, SIDE_ENEMY);
  }
  // elite affixes: on-death effects
  if (elite && affixMask & AFFIX_BITS.splitting) {
    for (let k = 0; k < AFFIX_SPLIT_COUNT; k++) {
      const a = (k / AFFIX_SPLIT_COUNT) * TAU;
      spawnEnemy(ctx, def.id, x + dcos(a) * AFFIX_SPLIT_SPREAD, y + dsin(a) * AFFIX_SPLIT_SPREAD, false, objectiveWave);
    }
  }
  if (elite && affixMask & AFFIX_BITS.volatile) fireVolatileBurst(ctx, x, y);
  ctx.kills++;
  ctx.events.push({ type: 'kill', x, y, enemyDef: def.id, elite });
  if (killer >= 0) {
    const player = ctx.w.players[killer];
    player.kills++;
    if (ctx.skillFlags[killer].has('phantom') && player.keystoneWindow > 0) {
      player.dashCdT = 0;
      player.keystoneWindow = 0;
    }
  }
}

export function dropPickup(ctx: SimCtx, x: number, y: number, kind: number, amount: number): void {
  const p = ctx.w.pickups;
  const i = p.pool.alloc();
  if (i === 0) {
    // pool full: merge into the oldest xp gem so no XP is lost
    if (kind === 0) {
      for (let j = 1; j < p.pool.high; j++) {
        if (p.pool.alive[j] && p.kind[j] === 0) {
          p.amount[j] += amount;
          return;
        }
      }
    }
    return;
  }
  p.x[i] = x;
  p.y[i] = y;
  p.kind[i] = kind;
  p.amount[i] = amount;
  p.life[i] = 60;
  p.magnet[i] = -1;
}
